import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/* ──────────────────────────────────────────────────────────────
 * AppSkeleton – Global Loading Experience Framework
 *
 * MANDATORY: This is the single source of truth for all loading states.
 * All pages MUST use structured skeleton loaders, NOT spinners.
 *
 * Core Principle: Zero layout shift, predictable loading UX
 *
 * Available Components:
 *   AppSkeleton      – Base primitive with CVA variants
 *   TableSkeleton    – Grid-based table placeholder (matches AppTable)
 *   CardSkeleton     – Card grid placeholder
 *   MetricSkeleton   – KPI metric blocks
 *   DetailPageSkeleton – Detail page with tabs
 *   FormSkeleton     – Form fields grid
 *   DashboardSkeleton – Full dashboard layout
 *   WorkspacePanelSkeleton – Workspace stats + cards
 *   ListPageSkeleton – Filter bar + table (NEW)
 *   TabsSkeleton     – Tab bar + content (NEW)
 *   AuthGuardSkeleton – Minimal centered loading (NEW)
 *
 * Page Mapping:
 *   Use PAGE_SKELETON_MAP to get the correct skeleton for each page type.
 *
 * Usage:
 *   <AppSkeleton variant="text" size="md" />
 *   <TableSkeleton rows={6} columns={5} />
 *   <ListPageSkeleton columns={5} />
 *   <AuthGuardSkeleton message="Verifying access..." />
 * ──────────────────────────────────────────────────────────── */

// ─── CVA-Based Skeleton Primitive ─────────────────────────────

const skeletonVariants = cva(
  'animate-pulse bg-muted rounded-md',
  {
    variants: {
      size: {
        sm: 'h-3',
        md: 'h-4',
        lg: 'h-6',
        xl: 'h-8',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

export type SkeletonSize = 'sm' | 'md' | 'lg' | 'xl';

const PULSE = 'animate-pulse bg-muted rounded-md';

// ─── Base Skeleton Primitive ──────────────────────────────────

export interface AppSkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonVariants> {
  variant?: 'text' | 'avatar' | 'card' | 'table-row' | 'metric' | 'detail' | 'badge';
}

export function AppSkeleton({
  variant = 'text',
  size,
  className,
  ...props
}: AppSkeletonProps) {
  switch (variant) {
    case 'avatar':
      return <div className={cn(PULSE, 'rounded-full h-8 w-8', className)} aria-hidden="true" {...props} />;
    case 'badge':
      return <div className={cn(PULSE, 'h-5 w-16 rounded-full', className)} aria-hidden="true" {...props} />;
    case 'metric':
      return <MetricSkeleton count={1} className={className} />;
    case 'card':
      return <CardSkeleton count={1} className={className} />;
    case 'table-row':
      return <TableRowSkeleton columns={5} />;
    case 'detail':
      return <DetailPageSkeleton />;
    default:
      return (
        <div
          className={cn(skeletonVariants({ size }), 'w-full', className)}
          aria-hidden="true"
          {...props}
        />
      );
  }
}

// ─── Table Skeleton (Grid-Based for Zero Layout Shift) ────────

export interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showAvatar?: boolean;
  showActions?: boolean;
  className?: string;
}

/**
 * TableSkeleton - Grid-based table skeleton that matches AppTable layout
 *
 * CRITICAL: Uses CSS Grid to maintain exact column alignment
 * Prevents layout shift when data loads
 */
export function TableSkeleton({
  rows = 6,
  columns = 5,
  showAvatar = false,
  showActions = true,
  className,
}: TableSkeletonProps) {
  return (
    <div
      className={cn('w-full overflow-hidden rounded-xl border border-border-hairline bg-surface-premium', className)}
      role="status"
      aria-busy="true"
      aria-label="Loading table data"
    >
      {/* Header row - matches AppTable header styling */}
      <div
        className="grid items-center gap-4 px-4 py-3 bg-surface-premium-muted border-b border-border-hairline"
        style={{ gridTemplateColumns: `${showAvatar ? '32px ' : ''}repeat(${columns}, minmax(0, 1fr))${showActions ? ' 80px' : ''}` }}
      >
        {showAvatar && <div className={cn(PULSE, 'rounded-full h-4 w-4')} aria-hidden="true" />}
        {Array.from({ length: columns }).map((_, i) => (
          <div
            key={i}
            className={cn(PULSE, 'h-3', i === 0 ? 'w-3/4' : 'w-2/3')}
            aria-hidden="true"
          />
        ))}
        {showActions && <div className={cn(PULSE, 'h-3 w-12 ml-auto')} aria-hidden="true" />}
      </div>

      {/* Data rows */}
      {Array.from({ length: rows }).map((_, ri) => (
        <div
          key={ri}
          className="grid items-center gap-4 px-4 py-3 border-b border-border last:border-b-0"
          style={{ gridTemplateColumns: `${showAvatar ? '32px ' : ''}repeat(${columns}, minmax(0, 1fr))${showActions ? ' 80px' : ''}` }}
        >
          {showAvatar && <div className={cn(PULSE, 'rounded-full h-7 w-7')} aria-hidden="true" />}
          {Array.from({ length: columns }).map((_, ci) => (
            <div
              key={ci}
              className={cn(
                PULSE,
                'h-3',
                ci === 0 ? 'w-4/5' : ci === columns - 1 ? 'w-1/2' : 'w-3/4'
              )}
              aria-hidden="true"
            />
          ))}
          {showActions && (
            <div className="flex items-center justify-end gap-1" aria-hidden="true">
              <div className={cn(PULSE, 'h-6 w-6 rounded')} />
              <div className={cn(PULSE, 'h-6 w-6 rounded')} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function TableRowSkeleton({ columns = 5 }: { columns?: number }) {
  return (
    <div
      className="grid items-center gap-4 px-4 py-3"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      aria-hidden="true"
    >
      {Array.from({ length: columns }).map((_, ci) => (
        <div key={ci} className={cn(PULSE, 'h-3', ci === 0 ? 'w-4/5' : 'w-3/4')} />
      ))}
    </div>
  );
}

// ─── Card Skeleton ────────────────────────────────────────────

export interface CardSkeletonProps {
  count?: number;
  className?: string;
}

export function CardSkeleton({ count = 3, className }: CardSkeletonProps) {
  return (
    <div
      className={cn('grid gap-4', count === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', className)}
      role="status"
      aria-busy="true"
      aria-label="Loading cards"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-2xl border border-border bg-card p-4 space-y-3"
        >
          <div className={cn(PULSE, 'h-4 w-3/5')} aria-hidden="true" />
          <div className={cn(PULSE, 'h-3 w-4/5')} aria-hidden="true" />
          <div className={cn(PULSE, 'h-3 w-2/5')} aria-hidden="true" />
          <div className="flex justify-end pt-1">
            <div className={cn(PULSE, 'h-7 w-20 rounded')} aria-hidden="true" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Metric / KPI Skeleton ────────────────────────────────────

export interface MetricSkeletonProps {
  count?: number;
  className?: string;
}

export function MetricSkeleton({ count = 4, className }: MetricSkeletonProps) {
  return (
    <div
      className={cn('grid gap-4 grid-cols-2 lg:grid-cols-4', className)}
      role="status"
      aria-busy="true"
      aria-label="Loading metrics"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-2xl border border-border bg-card p-4 space-y-2"
        >
          <div className={cn(PULSE, 'h-8 w-20')} aria-hidden="true" />
          <div className={cn(PULSE, 'h-3 w-28')} aria-hidden="true" />
        </div>
      ))}
    </div>
  );
}

// ─── Detail Page Skeleton ─────────────────────────────────────

export interface DetailPageSkeletonProps {
  className?: string;
}

export function DetailPageSkeleton({ className }: DetailPageSkeletonProps) {
  return (
    <div className={cn('space-y-4', className)} role="status" aria-busy="true" aria-label="Loading details">
      {/* Title + Subtitle */}
      <div className="space-y-2">
        <div className={cn(PULSE, 'h-6 w-64')} aria-hidden="true" />
        <div className={cn(PULSE, 'h-4 w-96')} aria-hidden="true" />
      </div>

      {/* Summary cards grid */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-border bg-card p-4 space-y-2">
            <div className={cn(PULSE, 'h-4 w-20')} aria-hidden="true" />
            <div className={cn(PULSE, 'h-5 w-28')} aria-hidden="true" />
          </div>
        ))}
      </div>

      {/* Tabs skeleton */}
      <div className="space-y-4">
        <div className="flex gap-4 border-b border-border pb-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={cn(PULSE, 'h-4 rounded', i === 0 ? 'w-20' : 'w-16')} aria-hidden="true" />
          ))}
        </div>

        {/* Tab content */}
        <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-4">
              <div className={cn(PULSE, 'h-3 w-28')} aria-hidden="true" />
              <div className={cn(PULSE, 'h-3 w-48')} aria-hidden="true" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Form Skeleton ────────────────────────────────────────────

export interface FormSkeletonProps {
  fields?: number;
  columns?: 1 | 2 | 3;
  className?: string;
}

export function FormSkeleton({ fields = 6, columns = 2, className }: FormSkeletonProps) {
  const gridClass = columns === 1 ? 'grid-cols-1' : columns === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  return (
    <div
      className={cn('rounded-2xl border border-border bg-card p-4 space-y-4', className)}
      role="status"
      aria-busy="true"
      aria-label="Loading form"
    >
      <div className={cn(PULSE, 'h-5 w-48 mb-2')} aria-hidden="true" />
      <div className={cn('grid gap-4', gridClass)}>
        {Array.from({ length: fields }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <div className={cn(PULSE, 'h-3 w-24')} aria-hidden="true" />
            <div className={cn(PULSE, 'h-9 w-full rounded-md')} aria-hidden="true" />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <div className={cn(PULSE, 'h-9 w-20 rounded-md')} aria-hidden="true" />
        <div className={cn(PULSE, 'h-9 w-24 rounded-md')} aria-hidden="true" />
      </div>
    </div>
  );
}

// ─── Dashboard Skeleton (full page) ──────────────────────────

export interface DashboardSkeletonProps {
  className?: string;
}

export function DashboardSkeleton({ className }: DashboardSkeletonProps) {
  return (
    <div className={cn('space-y-4', className)} role="status" aria-busy="true" aria-label="Loading dashboard">
      {/* KPI Row */}
      <MetricSkeleton count={4} />

      {/* Chart + Table */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        {/* Chart placeholder */}
        <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
          <div className={cn(PULSE, 'h-4 w-36')} aria-hidden="true" />
          <div className={cn(PULSE, 'h-48 w-full rounded-lg')} aria-hidden="true" />
        </div>
        {/* Mini-table */}
        <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
          <div className={cn(PULSE, 'h-4 w-40')} aria-hidden="true" />
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-3">
              <div className={cn(PULSE, 'h-3 w-28')} aria-hidden="true" />
              <div className={cn(PULSE, 'h-3 w-20')} aria-hidden="true" />
            </div>
          ))}
        </div>
      </div>

      {/* Full-width table */}
      <TableSkeleton rows={5} columns={5} />
    </div>
  );
}

// ─── Workspace Panel Skeleton ─────────────────────────────────

export interface WorkspacePanelSkeletonProps {
  className?: string;
}

export function WorkspacePanelSkeleton({ className }: WorkspacePanelSkeletonProps) {
  return (
    <div className={cn('space-y-4', className)} role="status" aria-busy="true" aria-label="Loading workspace">
      {/* Stats row */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-3 space-y-1.5">
            <div className={cn(PULSE, 'h-6 w-14')} aria-hidden="true" />
            <div className={cn(PULSE, 'h-3 w-20')} aria-hidden="true" />
          </div>
        ))}
      </div>
      {/* Action cards */}
      <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-3 flex items-center gap-3">
            <div className={cn(PULSE, 'h-8 w-8 rounded-lg flex-shrink-0')} aria-hidden="true" />
            <div className="flex-1 space-y-1.5">
              <div className={cn(PULSE, 'h-3.5 w-28')} aria-hidden="true" />
              <div className={cn(PULSE, 'h-3 w-40')} aria-hidden="true" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── List Page Skeleton (Filter Bar + Table) ──────────────────

export interface ListPageSkeletonProps {
  columns?: number;
  rows?: number;
  showFilters?: boolean;
  showSearch?: boolean;
  className?: string;
}

/**
 * ListPageSkeleton - Complete list page with filter bar and table
 *
 * Use for: HospitalListPage, PatientListPage, UserListPage, etc.
 */
export function ListPageSkeleton({
  columns = 5,
  rows = 8,
  showFilters = true,
  showSearch = true,
  className,
}: ListPageSkeletonProps) {
  return (
    <div className={cn('space-y-4', className)} role="status" aria-busy="true" aria-label="Loading list">
      {/* Page header skeleton */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className={cn(PULSE, 'h-6 w-48')} aria-hidden="true" />
          <div className={cn(PULSE, 'h-3 w-72')} aria-hidden="true" />
        </div>
        <div className={cn(PULSE, 'h-9 w-28 rounded-md')} aria-hidden="true" />
      </div>

      {/* Filter bar skeleton */}
      {(showFilters || showSearch) && (
        <div className="flex flex-wrap items-center gap-3">
          {showSearch && (
            <div className={cn(PULSE, 'h-9 w-64 rounded-md')} aria-hidden="true" />
          )}
          {showFilters && (
            <>
              <div className={cn(PULSE, 'h-9 w-32 rounded-md')} aria-hidden="true" />
              <div className={cn(PULSE, 'h-9 w-32 rounded-md')} aria-hidden="true" />
              <div className={cn(PULSE, 'h-9 w-24 rounded-md')} aria-hidden="true" />
            </>
          )}
        </div>
      )}

      {/* Table skeleton */}
      <TableSkeleton rows={rows} columns={columns} showActions />
    </div>
  );
}

// ─── Tabs Skeleton ────────────────────────────────────────────

export interface TabsSkeletonProps {
  tabCount?: number;
  contentHeight?: string;
  className?: string;
}

/**
 * TabsSkeleton - Tab navigation with content placeholder
 *
 * Use for: Detail pages with tabs, settings pages, etc.
 */
export function TabsSkeleton({
  tabCount = 4,
  contentHeight = 'h-64',
  className,
}: TabsSkeletonProps) {
  return (
    <div className={cn('space-y-4', className)} role="status" aria-busy="true" aria-label="Loading tabs">
      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-border pb-px">
        {Array.from({ length: tabCount }).map((_, i) => (
          <div
            key={i}
            className={cn(
              PULSE,
              'h-9 rounded-t-md px-4',
              i === 0 ? 'w-24 bg-muted' : 'w-20'
            )}
            aria-hidden="true"
          />
        ))}
      </div>

      {/* Tab content placeholder */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className={cn(PULSE, contentHeight, 'w-full rounded-lg')} aria-hidden="true" />
      </div>
    </div>
  );
}

// ─── Auth Guard Skeleton ──────────────────────────────────────

export interface AuthGuardSkeletonProps {
  message?: string;
  className?: string;
}

/**
 * AuthGuardSkeleton - Minimal centered loading for auth/permission checks
 *
 * Use for: ProgramModuleGuard, RoleGovernanceGuard, WorkflowGuard
 * This is the ONLY acceptable spinner-style loader (for brief auth checks)
 */
export function AuthGuardSkeleton({
  message = 'Verifying access...',
  className,
}: AuthGuardSkeletonProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center min-h-[200px] p-8',
        className
      )}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      {/* Pulsing logo/icon placeholder */}
      <div className={cn(PULSE, 'h-12 w-12 rounded-full mb-4')} aria-hidden="true" />
      <div className={cn(PULSE, 'h-4 w-32')} aria-hidden="true" />
      <span className="sr-only">{message}</span>
    </div>
  );
}

// ─── Page Skeleton Map (Registry) ─────────────────────────────

/**
 * PAGE_SKELETON_MAP - Central registry mapping page types to skeletons
 *
 * MANDATORY: Every page type must have a corresponding skeleton.
 * Use this to ensure consistent loading experiences across modules.
 */
export const PAGE_SKELETON_MAP = {
  // List/Table pages
  list: ListPageSkeleton,
  table: TableSkeleton,
  
  // Detail/View pages
  detail: DetailPageSkeleton,
  view: DetailPageSkeleton,
  
  // Form/Edit pages
  form: FormSkeleton,
  edit: FormSkeleton,
  create: FormSkeleton,
  
  // Dashboard pages
  dashboard: DashboardSkeleton,
  
  // Workspace pages
  workspace: WorkspacePanelSkeleton,
  
  // Card grid pages
  cards: CardSkeleton,
  
  // Metric/KPI pages
  metrics: MetricSkeleton,
  
  // Auth/Guard loading
  guard: AuthGuardSkeleton,
  
  // Tab-based pages
  tabs: TabsSkeleton,
} as const;

export type PageSkeletonType = keyof typeof PAGE_SKELETON_MAP;

/**
 * getPageSkeleton - Get the appropriate skeleton component for a page type
 *
 * @example
 * const Skeleton = getPageSkeleton('list');
 * return <Skeleton columns={5} rows={10} />;
 */
export function getPageSkeleton(type: PageSkeletonType) {
  return PAGE_SKELETON_MAP[type];
}

// ─── Skeleton CVA Export ──────────────────────────────────────

export { skeletonVariants };
